import { getSharedLang, type Lang } from '../../../lib/base-i18n';

/**
 * 이동시간 칸의 글자 (2026-10-13).
 *
 * ⚠ `lib/calendar-i18n.ts` 는 여러 세션이 «끝에 덧붙이는» 파일이라 충돌을 피해 여기 따로 둔다.
 *   lang 은 같은 공유 상태(`getSharedLang`)를 읽으므로 화면 언어와 늘 같다.
 */
const M = {
  heading: { ko: '이동시간', en: 'Travel time', ja: '移動時間' },
  from_worksite: { ko: '{name}에서 출발', en: 'From {name}', ja: '{name}から出発' },
  car: { ko: '자동차', en: 'Car', ja: '車' },
  walk: { ko: '도보', en: 'Walk', ja: '徒歩' },
  approx: { ko: '약 {v}', en: 'about {v}', ja: '約{v}' },
  minutes: { ko: '{m}분', en: '{m} min', ja: '{m}分' },
  hours: { ko: '{h}시간', en: '{h} h', ja: '{h}時間' },
  hours_minutes: { ko: '{h}시간 {m}분', en: '{h} h {m} min', ja: '{h}時間{m}分' },
  km: { ko: '{v}km', en: '{v} km', ja: '{v}km' },
  m: { ko: '{v}m', en: '{v} m', ja: '{v}m' },
  walk_note: { ko: '도보는 직선거리로 어림한 값입니다', en: 'Walking time is estimated from straight-line distance', ja: '徒歩は直線距離からの概算です' },
  to_place: { ko: '도착: {name}', en: 'To: {name}', ja: '到着: {name}' },
  to_place_note: { ko: '장소 이름으로 찾은 위치입니다', en: 'Location found by place name', ja: '場所名で検索した位置です' },
  map_aria: { ko: '출발지에서 일정 장소까지 경로 지도', en: 'Route map from origin to the event location', ja: '出発地から予定場所までの経路地図' },
  map_zoom_in: { ko: '지도 확대', en: 'Zoom in', ja: '地図を拡大' },
  map_zoom_out: { ko: '지도 축소', en: 'Zoom out', ja: '地図を縮小' },
} satisfies Record<string, Record<Lang, string>>;

export type TravelI18nKey = keyof typeof M;

export function tTravel(key: TravelI18nKey, params?: Record<string, string | number>): string {
  const raw = M[key][getSharedLang()] ?? M[key].ko;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, n: string) => (params[n] === undefined ? `{${n}}` : String(params[n])));
}

/** 초 → 「1시간 5분」「12분」. 1분 미만은 1분. */
export function formatDuration(seconds: number): string {
  const total = Math.max(1, Math.round(seconds / 60));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return tTravel('minutes', { m });
  if (m === 0) return tTravel('hours', { h });
  return tTravel('hours_minutes', { h, m });
}

/** 미터 → 「12.3km」「850m」. */
export function formatDistance(meters: number): string {
  if (meters < 1000) return tTravel('m', { v: Math.round(meters) });
  const km = meters / 1000;
  return tTravel('km', { v: km >= 100 ? Math.round(km) : Math.round(km * 10) / 10 });
}
